"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const WidgetError_1 = require("../Error/WidgetError");
/**
 * Creates application with container encapsulation.
 *
 * @param {Container} baseContainer
 * @return {App}
 */
function default_1(baseContainer) {
    /**
     * Primary application class.
     */
    class App {
        /**
         * @inheritDoc
         */
        createWidget(containerId, config = {}) {
            if (!containerId) {
                throw new WidgetError_1.default(`Container ID is required (createdWidget() first argument.`);
            }
            if (!config) {
                throw new WidgetError_1.default(`Config is required (createdWidget() second argument.`);
            }
            // Create a temporary container specifically for configuration initialization.
            const initializerContainer = baseContainer.clone();
            initializerContainer.compile();
            let finalContainer;
            config.containerId = containerId;
            let widgetState;
            let widgetConfig;
            return new Promise((resolve, reject) => {
                widgetConfig = initializerContainer.get("factory.widget_config").create(config);
                // We clone the container so there are no possible conflicts between
                // different widget instances. This will result in different instances of
                // singleton services per widget instance.
                finalContainer = baseContainer.clone();
                // Create the final container with additional params from configuration.
                finalContainer.setParameters(widgetConfig.getContainerParameters());
                finalContainer.compile();
                const mappingValidator = finalContainer.get("mapping_validator");
                mappingValidator.validate(widgetConfig, "Invalid widget configuration provided");
                widgetState = finalContainer.get("factory.widget_state").create();
                // Initialize the widget config and state.
                finalContainer.get("widget.initializer")
                    .initialize(widgetConfig, widgetState)
                    .then(() => {
                    widgetState.setInitializersRan();
                    // Create the widget from config and state.
                    resolve(finalContainer.get("factory.widget")
                        .create(widgetConfig, widgetState));
                })
                    .catch((err) => reject(err));
            })
                .then((widget) => {
                // Create all extensions for the widget.
                const promises = finalContainer.get("plugin.manager.widget_extension")
                    .createAll().map((extension) => extension.initialize(widget));
                return Promise.all(promises)
                    // Finally return the widget facade for the client application.
                    .then(() => {
                    widgetState.setExtensionsLoaded();
                    return widget.getFacade();
                });
            })
                // If anything fails we attempt to handle the failure. At this point we won't
                // run any initializer or extension but create a blank widget instance.
                .catch((error) => {
                if (!finalContainer) {
                    throw WidgetError_1.default.extend('Failed compiling service container! ', error);
                }
                // Attempt to use previous config and state.
                const nWidgetConfig = widgetConfig || finalContainer.get("factory.widget_config").create(config);
                const nWidgetState = widgetState || finalContainer.get("factory.widget_state").create();
                const widget = finalContainer.get("factory.widget")
                    .create(nWidgetConfig, nWidgetState);
                nWidgetState.setExtensionsLoaded(false);
                return finalContainer.get("error.handler").handle(error, widget)
                    .then((handled) => {
                    if (handled) {
                        // This error must be logged otherwise it will never display.
                        console.error(error);
                        // If handled we emit the error on the widget and return it to the host site
                        // as error specific flow might still apply.
                        return widget.getFacade();
                    }
                    else {
                        // If the error could not be handled we throw to the host site.
                        throw error;
                    }
                });
            });
        }
    }
    return new App;
}
exports.default = default_1;
